<template>
  <div class="teacher-typer-page">
    <shared-header />
    <div class="teacher-assign-typing">
    <el-card class="history-card" shadow="hover">
      <template #header>
        <div class="history-header">
          <span class="card-title">历史作业</span>
          <el-button link type="primary" :loading="historyLoading" @click="loadHistoryAssignments">
            刷新
          </el-button>
        </div>
        <p class="card-desc">
          点击标题可填入该作业内容；提交时仅将所选学生绑定到该作业，不会新建重复作业。
        </p>
      </template>
      <div v-loading="historyLoading">
        <div v-if="!historyLoading && !historyAssignments.length" class="hint-muted">
          暂无历史作业，填写下方表单可新建并布置。
        </div>
        <el-scrollbar v-else max-height="280px">
          <ul class="history-title-list">
            <li
              v-for="item in historyAssignments"
              :key="item.id"
              class="history-title-item"
              :class="{ 'is-active': reuseAssignmentId === item.id }"
              @click="applyHistoryAssignment(item)"
            >
              <div class="history-title-main">
                <div class="history-title-text">
                  {{ item.title || `作业 #${item.id}` }}
                </div>
                <div class="history-subtitle">
                  {{ formatHistoryMeta(item) }}
                </div>
              </div>
            </li>
          </ul>
        </el-scrollbar>
      </div>
      <p v-if="reuseAssignmentId" class="reuse-hint">
        已选历史作业 #{{ reuseAssignmentId }}，提交时将只绑定新选学生。
        <el-button link type="primary" @click="clearReuseAssignment">改为新建作业</el-button>
      </p>
    </el-card>

    <el-card class="assign-card" shadow="hover">
      <template #header>
        <span class="card-title">教师布置打字练习</span>
        <p class="card-desc">
          先添加学生并填写作业标题。在「文本素材」里放入给 AI 的原材料（单词、提纲等），一键生成「打字文本」；学生端将对照「打字文本」完成练习。最后设定达标参数并提交。
        </p>
      </template>

      <el-form ref="formRef" :model="form" :rules="rules" label-width="160px" label-position="left" class="assign-form">
        <el-form-item label="选择学生" prop="selectedStudents">
          <el-select
            v-model="remoteSelect.value"
            class="student-remote-select"
            filterable
            remote
            clearable
            :remote-method="searchStudents"
            :loading="remoteSelect.loading"
            placeholder="输入姓名远程搜索并选择学生"
            value-key="userId"
            @change="onRemoteStudentChange"
            @keydown.stop
          >
            <el-option
              v-for="item in remoteSelect.options"
              :key="item.userId"
              :label="`${item.userName} : ${item.chineseName}`"
              :value="item"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="已选学生">
          <div v-if="!form.selectedStudents.length" class="hint-muted">尚未添加学生</div>
          <div v-else class="selected-tags">
            <el-tag
              v-for="s in form.selectedStudents"
              :key="s.userId"
              class="student-tag"
              closable
              @close="removeStudent(s)"
            >
              {{ s.chineseName }}
            </el-tag>
          </div>
        </el-form-item>

        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="例如：没有例如" @keydown.stop />
        </el-form-item>

        <el-form-item label="文本素材" prop="originalText">
          <p class="form-item-tip">
            给AI的生成最终英文文本的依据，可填入希望文本包含的单词（表）、文本的提纲或一小段英文，也可为空; 然后点击下方“生成文本”按钮，AI会生成最终用于打字练习的英文文本并写入下方的「打字文本」框中。
          </p>
          <el-input
            v-model="form.originalText"
            type="textarea"
            :rows="8"
            placeholder="例如：本课单词列表，或一段作文模版，或你希望 AI 围绕展开的一小段提示…"
            @keydown.stop
          />
          <div class="original-text-actions">
            <el-button
              type="primary"
              plain
              size="small"
              :loading="aiGenerateLoading"
              :disabled="!(form.originalText || '').trim()"
              @click="generateAiFromOriginal"
            >
            生成文本
            </el-button>
          </div>
        </el-form-item>

        <el-form-item label="打字文本" prop="AIgeneratedText">
          <p class="form-item-tip">
            用于打字练习的文本，可由“文本素材”一键生成，也可将需要学生练习的特定文本在此处输入，同时支持编辑和修改
          </p>
          <div class="typing-text-toolbar">
            <el-button
              type="primary"
              plain
              size="small"
              :loading="samplesDialogLoading"
              @click="openSamplesDialog"
            >
              从样本库选择
            </el-button>
          </div>
          <el-input
            v-model="form.AIgeneratedText"
            type="textarea"
            :rows="6"
            placeholder="此处应为完整英文段落，即学生打开作业后看到的打字内容。"
            @keydown.stop
          />
        </el-form-item>

        <el-dialog
          v-model="samplesDialogVisible"
          title="选择打字样本"
          width="520px"
          destroy-on-close
          append-to-body
        >
          <div v-if="samplesDialogLoading" class="hint-muted">正在加载样本列表…</div>
          <div v-else-if="!sampleList.length" class="hint-muted">暂无样本，可先在 Typer 菜单「添加样本」中录入。</div>
          <el-scrollbar v-else max-height="420px">
            <ul class="sample-title-list">
              <li
                v-for="s in sampleList"
                :key="s.id"
                class="sample-title-item"
              >
                <div class="sample-title-main" @click="applySample(s)">
                  <div class="sample-title-text">
                    {{ (s.title && String(s.title).trim()) || `样本 #${s.id}` }}
                  </div>
                  <div class="sample-subtitle">
                    类型：{{ (s.type && String(s.type).trim()) || '未填写' }}
                    <span class="sample-subtitle-gap">|</span>
                    分值：{{ Number(s.score) || 0 }}
                  </div>
                </div>
                <div class="sample-actions">
                  <el-button
                    size="small"
                    link
                    type="primary"
                    :disabled="sampleActionLoading"
                    @click.stop="openEditSample(s)"
                  >
                    编辑
                  </el-button>
                  <el-button
                    size="small"
                    link
                    type="danger"
                    :disabled="sampleActionLoading"
                    @click.stop="deleteSample(s)"
                  >
                    删除
                  </el-button>
                </div>
              </li>
            </ul>
          </el-scrollbar>
        </el-dialog>
        <el-dialog
          v-model="editSampleVisible"
          title="编辑打字样本"
          width="640px"
          destroy-on-close
          append-to-body
        >
          <el-form
            ref="sampleEditFormRef"
            :model="sampleEditForm"
            :rules="sampleEditRules"
            label-width="88px"
            label-position="left"
          >
            <el-form-item label="标题" prop="title">
              <el-input v-model="sampleEditForm.title" @keydown.stop />
            </el-form-item>
            <el-form-item label="正文" prop="text">
              <el-input
                v-model="sampleEditForm.text"
                type="textarea"
                :rows="8"
                @keydown.stop
              />
            </el-form-item>
            <el-form-item label="分值" prop="score">
              <el-input-number
                v-model="sampleEditForm.score"
                :min="0"
                :max="100"
                :step="0.5"
                :precision="2"
              />
            </el-form-item>
            <el-form-item label="类型" prop="type">
              <el-input v-model="sampleEditForm.type" @keydown.stop />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="editSampleVisible = false">取消</el-button>
            <el-button type="primary" :loading="editSampleSubmitting" @click="submitEditSample">
              保存
            </el-button>
          </template>
        </el-dialog>

        <el-form-item label="区分大小写">
          <el-switch v-model="form.caseSensitive" active-text="是" inactive-text="否" />
        </el-form-item>

        <el-form-item label="WPM 达标线" prop="wpm">
          <el-input-number v-model="form.wpm" :min="1" :max="300" />
          <span class="field-hint">学生需达到的每分钟词数下限</span>
        </el-form-item>

        <el-form-item label="倒计时（秒）" prop="countdown">
          <el-input-number v-model="form.countdown" :min="1" :max="86400" />
          <span class="field-hint">本次练习总计时</span>
        </el-form-item>

        <el-form-item label="准确率标准" prop="standardRate">
          <el-input-number v-model="form.standardRate" :min="0" :max="100" />
          <span class="field-hint">达标所需的准确率百分比</span>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="submitLoading" @click="onSubmit">
            {{ reuseAssignmentId ? '绑定学生到作业' : '提交布置' }}
          </el-button>
          <el-button @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { buildToeflArticlePromptFromOriginal } from './toeflArticlePrompt.js'
import SharedHeader from '../../shared/SharedHeader.vue'
import {
  formatTyperDateToMinute,
  resolveTyperAssignmentId,
  resolveTyperAiText,
  unwrapApiData
} from '../../../utils/typerNormalize'

export default {
  name: 'TeacherAssignTypingPractice',
  components: { SharedHeader },
  data() {
    const validateStudents = (_rule, value, callback) => {
      if (!value || !value.length) {
        callback(new Error('请至少选择一名学生'))
      } else {
        callback()
      }
    }
    return {
      remoteSelect: {
        value: null,
        options: [],
        loading: false
      },
      submitLoading: false,
      historyAssignments: [],
      historyLoading: false,
      reuseAssignmentId: null,
      aiGenerateLoading: false,
      samplesDialogVisible: false,
      samplesDialogLoading: false,
      sampleActionLoading: false,
      sampleList: [],
      editSampleVisible: false,
      editSampleSubmitting: false,
      sampleEditForm: {
        id: 0,
        title: '',
        text: '',
        score: 0,
        type: ''
      },
      form: {
        selectedStudents: [],
        title: '',
        typerSampleId: 0,
        originalText: '',
        AIgeneratedText: '',
        caseSensitive: true,
        wpm: 40,
        countdown: 300,
        standardRate: 90
      },
      rules: {
        title: [{ required: true, message: '请填写作业标题', trigger: 'blur' }],
        selectedStudents: [{ validator: validateStudents, trigger: 'change' }],
        wpm: [{ required: true, message: '请设定 WPM', trigger: 'change' }],
        countdown: [{ required: true, message: '请设定倒计时', trigger: 'change' }],
        standardRate: [{ required: true, message: '请设定准确率标准', trigger: 'change' }]
      },
      sampleEditRules: {
        title: [{ required: true, message: '请填写标题', trigger: 'blur' }],
        text: [{ required: true, message: '请填写正文', trigger: 'blur' }],
        type: [{ required: true, message: '请填写类型', trigger: 'blur' }]
      }
    }
  },
  created() {
    const pair = [{ validator: this.validateMaterialOrBody, trigger: 'blur' }]
    this.rules.originalText = pair
    this.rules.AIgeneratedText = pair
  },
  mounted() {
    this.loadHistoryAssignments()
    this.applyRoutePrefill(this.$route)
  },
  watch: {
    $route(to) {
      if (to.path === '/vocTyper/teacher-assign') {
        this.applyRoutePrefill(to)
      }
    }
  },
  methods: {
    validateMaterialOrBody(_rule, _value, callback) {
      const material = (this.form.originalText || '').trim()
      const body = (this.form.AIgeneratedText || '').trim()
      if (!material && !body) {
        callback(
          new Error(
            '请填写「文本素材」并生成打字文本，或直接在「打字文本」粘贴完整英文'
          )
        )
      } else {
        callback()
      }
    },
    applyRoutePrefill(route) {
      const q = route.query || {}
      if (q.words) {
        try {
          const arr = JSON.parse(q.words)
          if (Array.isArray(arr) && arr.length) {
            this.form.originalText = arr
              .map((w) => String(w || '').trim())
              .filter(Boolean)
              .join(' ')
            this.$nextTick(() => {
              this.$refs.formRef?.validateField('originalText')
            })
          }
        } catch (e) {
          console.warn('applyRoutePrefill words', e)
        }
      }
      const sid = q.prefStudentId
      if (!sid) return
      const userId = Number(sid)
      if (!Number.isFinite(userId) || userId <= 0) return
      if (this.form.selectedStudents.some((s) => s.userId === userId)) return
      const userName = (q.prefStudentUserName || '').trim() || String(sid)
      const chineseName =
        (q.prefStudentChineseName || '').trim() ||
        (q.prefStudentUserName || '').trim() ||
        String(sid)
      this.form.selectedStudents.push({
        userId,
        userName,
        chineseName,
        id: userId
      })
      this.$nextTick(() => {
        this.$refs.formRef?.validateField('selectedStudents')
      })
    },
    searchStudents(query) {
      const q = (query || '').trim()
      if (!q) {
        this.remoteSelect.options = []
        return
      }
      this.remoteSelect.loading = true
      this.$store
        .dispatch('Admin/getAllStudentsByCriteria', { studentName: q })
        .then((response) => {
          this.remoteSelect.loading = false
          this.remoteSelect.options = response.data || []
        })
        .catch((err) => {
          console.error(err)
          this.remoteSelect.loading = false
          this.remoteSelect.options = []
        })
    },

    onRemoteStudentChange(student) {
      if (!student || !student.userId) {
        this.remoteSelect.value = null
        return
      }
      const exists = this.form.selectedStudents.some((s) => s.userId === student.userId)
      if (exists) {
        ElMessage.warning('该学生已在列表中')
        this.remoteSelect.value = null
        return
      }
      this.form.selectedStudents.push({
        ...student,
        id: student.userId
      })
      this.remoteSelect.value = null
      this.remoteSelect.options = []
      this.$refs.formRef?.validateField('selectedStudents')
    },

    removeStudent(s) {
      const i = this.form.selectedStudents.findIndex((x) => x.userId === s.userId)
      if (i >= 0) this.form.selectedStudents.splice(i, 1)
      this.$refs.formRef?.validateField('selectedStudents')
    },

    openSamplesDialog() {
      this.samplesDialogVisible = true
      this.fetchSamples({ closeOnError: true })
    },
    fetchSamples({ closeOnError = false } = {}) {
      this.samplesDialogLoading = true
      this.sampleList = []
      this.$store
        .dispatch('TyperSample/getSamples')
        .then((response) => {
          const data = response.data
          this.sampleList = Array.isArray(data) ? data : []
        })
        .catch((err) => {
          console.error(err)
          const d = err.response?.data
          const msg =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '加载样本失败'
          ElMessage.error(String(msg))
          if (closeOnError) this.samplesDialogVisible = false
        })
        .finally(() => {
          this.samplesDialogLoading = false
        })
    },

    applySample(sample) {
      if (!sample) return
      const text = (sample.text != null ? String(sample.text) : '').trim()
      if (!text) {
        ElMessage.warning('该样本没有正文')
        return
      }
      this.form.AIgeneratedText = sample.text != null ? String(sample.text) : ''
      const sid = Number(sample.id)
      this.form.typerSampleId = Number.isFinite(sid) && sid > 0 ? sid : 0
      this.samplesDialogVisible = false
      ElMessage.success('已填入「打字文本」')
      this.$nextTick(() => {
        this.$refs.formRef?.validateField('AIgeneratedText')
      })
    },
    openEditSample(sample) {
      if (!sample) return
      const sid = Number(sample.id)
      if (!Number.isFinite(sid) || sid <= 0) {
        ElMessage.warning('无效的样本编号，无法编辑')
        return
      }
      this.sampleEditForm = {
        id: sid,
        title: sample.title != null ? String(sample.title) : '',
        text: sample.text != null ? String(sample.text) : '',
        score: Number(sample.score) || 0,
        type: sample.type != null ? String(sample.type) : ''
      }
      this.editSampleVisible = true
      this.$nextTick(() => {
        this.$refs.sampleEditFormRef?.clearValidate?.()
      })
    },
    submitEditSample() {
      this.$refs.sampleEditFormRef?.validate((valid) => {
        if (!valid) return
        const payload = {
          id: this.sampleEditForm.id,
          title: (this.sampleEditForm.title || '').trim(),
          text: (this.sampleEditForm.text || '').trim(),
          score: Number(this.sampleEditForm.score) || 0,
          type: (this.sampleEditForm.type || '').trim()
        }
        this.editSampleSubmitting = true
        this.$store
          .dispatch('TyperSample/updateSample', payload)
          .then(() => {
            ElMessage.success('样本已更新')
            this.editSampleVisible = false
            this.fetchSamples()
          })
          .catch((err) => {
            console.error(err)
            const d = err.response?.data
            const msg =
              (typeof d === 'string' && d) ||
              d?.message ||
              d?.title ||
              err.message ||
              '更新样本失败'
            ElMessage.error(String(msg))
          })
          .finally(() => {
            this.editSampleSubmitting = false
          })
      })
    },
    deleteSample(sample) {
      if (!sample) return
      const sid = Number(sample.id)
      if (!Number.isFinite(sid) || sid <= 0) {
        ElMessage.warning('无效的样本编号，无法删除')
        return
      }
      const title = (sample.title && String(sample.title).trim()) || `样本 #${sid}`
      ElMessageBox.confirm(
        `确认删除「${title}」吗？此操作不可恢复。`,
        '删除样本',
        {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消'
        }
      )
        .then(() => {
          this.sampleActionLoading = true
          return this.$store.dispatch('TyperSample/deleteSample', sid)
        })
        .then(() => {
          ElMessage.success('样本已删除')
          this.fetchSamples()
        })
        .catch((err) => {
          if (err === 'cancel' || err === 'close') return
          console.error(err)
          const d = err.response?.data
          const msg =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '删除样本失败'
          ElMessage.error(String(msg))
        })
        .finally(() => {
          this.sampleActionLoading = false
        })
    },

    generateAiFromOriginal() {
      const original = (this.form.originalText || '').trim()
      if (!original) {
        ElMessage.warning('请先填写文本素材')
        return
      }
      this.aiGenerateLoading = true
      ElMessage.info('正在根据文本素材生成打字文本…')
      this.$store
        .dispatch('DeepSeek/getDeepSeekResponse', {
          model: 'glm-5.3',
          messages: [{ role: 'user', content: buildToeflArticlePromptFromOriginal(original) }]
        })
        .then((response) => {
          const content = (response.data?.choices?.[0]?.message?.content || '').trim()
          if (!content) {
            throw new Error('empty')
          }
          this.form.AIgeneratedText = content
          this.form.typerSampleId = 0
          ElMessage.success('已写入「打字文本」')
        })
        .catch(() => {
          ElMessage.error('生成失败，请稍后重试')
        })
        .finally(() => {
          this.aiGenerateLoading = false
        })
    },

    loadHistoryAssignments() {
      const teacherId = parseInt(localStorage.getItem('userId'), 10)
      if (!Number.isFinite(teacherId) || teacherId <= 0) return
      this.historyLoading = true
      this.$store
        .dispatch('TyperAssignment/getTeacherAssignments', { teacherId })
        .then((response) => {
          const raw = unwrapApiData(response)
          const list = Array.isArray(raw) ? raw : []
          const byId = new Map()
          for (const item of list) {
            const id = resolveTyperAssignmentId(item)
            if (!id || byId.has(id)) continue
            byId.set(id, {
              ...item,
              id,
              title: item.title ?? item.assignmentTitle ?? '',
              AIgeneratedText: resolveTyperAiText(item),
              createdDate: item.createdDate ?? item.createdAt ?? item.assignedDate ?? ''
            })
          }
          this.historyAssignments = [...byId.values()].sort((a, b) => {
            const ta = new Date(a.createdDate || 0).getTime()
            const tb = new Date(b.createdDate || 0).getTime()
            return tb - ta
          })
        })
        .catch((err) => {
          console.error(err)
          const d = err.response?.data
          const msg =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '加载历史作业失败'
          ElMessage.error(String(msg))
        })
        .finally(() => {
          this.historyLoading = false
        })
    },

    formatHistoryMeta(item) {
      const parts = []
      const date = formatTyperDateToMinute(item.createdDate)
      if (date && date !== '—') parts.push(date)
      const mappings = item.mappings ?? item.typerAssignmentMappings
      if (Array.isArray(mappings) && mappings.length) {
        parts.push(`已绑定 ${mappings.length} 人`)
      }
      const wpm = Number(item.wpm)
      if (Number.isFinite(wpm) && wpm > 0) parts.push(`WPM ${wpm}`)
      return parts.length ? parts.join(' · ') : '历史作业'
    },

    applyHistoryAssignment(item) {
      if (!item) return
      const id = resolveTyperAssignmentId(item)
      if (!id) {
        ElMessage.warning('无效的历史作业')
        return
      }
      this.reuseAssignmentId = id
      this.form.title = item.title != null ? String(item.title) : ''
      this.form.originalText = item.originalText != null ? String(item.originalText) : ''
      this.form.AIgeneratedText = resolveTyperAiText(item)
      const sampleId = Number(item.typerSampleId)
      this.form.typerSampleId = Number.isFinite(sampleId) && sampleId > 0 ? sampleId : 0
      this.form.caseSensitive = !!(item.caseSensitive ?? item.isCaseSensitive)
      this.form.wpm = Number(item.wpm) || 40
      this.form.countdown = Number(item.countdown) || 300
      this.form.standardRate = Number(item.standardRate) || 90
      ElMessage.success('已填入该历史作业内容')
      this.$nextTick(() => {
        this.$refs.formRef?.validateField('title')
        this.$refs.formRef?.validateField('originalText')
        this.$refs.formRef?.validateField('AIgeneratedText')
      })
    },

    clearReuseAssignment() {
      this.reuseAssignmentId = null
      ElMessage.info('已切换为新建作业模式')
    },

    buildAssignmentPayload () {
      const rawTeacherId = localStorage.getItem('userId')
      const teacherId = parseInt(rawTeacherId, 10)
      return {
        teacherId: Number.isFinite(teacherId) ? teacherId : 0,
        title: this.form.title.trim(),
        typerSampleId: this.form.typerSampleId ?? 0,
        originalText: this.form.originalText,
        AIgeneratedText: (this.form.AIgeneratedText || '').trim(),
        aIgeneratedText: (this.form.AIgeneratedText || '').trim(),
        caseSensitive: !!this.form.caseSensitive,
        wpm: Number(this.form.wpm),
        countdown: Number(this.form.countdown),
        standardRate: Number(this.form.standardRate)
      }
    },

    onSubmit () {
      this.$refs.formRef?.validate((valid) => {
        if (!valid) return
        const assignment = this.buildAssignmentPayload()
        const studentIds = this.form.selectedStudents.map((s) => Number(s.userId))
        if (!assignment.teacherId) {
          ElMessage.error('无法获取当前教师账号，请重新登录后再试')
          return
        }
        if (!studentIds.length) {
          ElMessage.error('请至少选择一名学生')
          return
        }
        if (!(assignment.AIgeneratedText || '').trim()) {
          ElMessage.error('请填写或生成「打字文本」后再提交')
          return
        }
        this.submitLoading = true
        const dispatchName = this.reuseAssignmentId
          ? 'TyperAssignment/mapExistingAssignmentToStudents'
          : 'TyperAssignment/assignToStudents'
        const dispatchPayload = this.reuseAssignmentId
          ? {
              teacherTyperAssignmentId: this.reuseAssignmentId,
              studentIds
            }
          : { assignment, studentIds }
        this.$store
          .dispatch(dispatchName, dispatchPayload)
          .then((result) => {
            const id = result?.teacherTyperAssignmentId ?? this.reuseAssignmentId
            const actionLabel = this.reuseAssignmentId ? '绑定' : '布置'
            ElMessage.success(
              id
                ? `${actionLabel}成功，已绑定 ${studentIds.length} 名学生`
                : `${actionLabel}成功`
            )
            this.resetForm()
            this.loadHistoryAssignments()
          })
          .catch((err) => {
            const d = err.response?.data
            const msg =
              (typeof d === 'string' && d) ||
              d?.message ||
              d?.title ||
              err.message ||
              '提交失败，请稍后重试'
            ElMessage.error(String(msg))
          })
          .finally(() => {
            this.submitLoading = false
          })
      })
    },

    resetForm() {
      this.$refs.formRef?.resetFields()
      this.form.selectedStudents = []
      this.form.typerSampleId = 0
      this.reuseAssignmentId = null
      this.remoteSelect = { value: null, options: [], loading: false }
    }
  }
}
</script>

<style scoped lang="scss">
.teacher-typer-page {
  text-align: left;
}

.teacher-assign-typing {
  max-width: 880px;
  margin: 24px auto;
  padding: 0 16px;
}

.history-card {
  border-radius: 8px;
  margin-bottom: 16px;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.history-title-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.history-title-item {
  padding: 10px 12px;
  margin: 0;
  border-radius: 6px;
  cursor: pointer;
}

.history-title-item:hover,
.history-title-item.is-active {
  background: var(--el-fill-color-light);
}

.history-title-item.is-active .history-title-text {
  color: var(--el-color-primary);
}

.history-title-main {
  min-width: 0;
}

.history-title-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--el-text-color-primary);
  line-height: 1.45;
}

.history-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.reuse-hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.assign-card {
  border-radius: 8px;
}

.card-title {
  font-size: 1.15rem;
  font-weight: 600;
}

.card-desc {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.assign-form {
  margin-top: 8px;
}

.student-remote-select {
  width: 100%;
  max-width: 420px;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.student-tag {
  margin: 0;
}

.hint-muted {
  color: var(--el-text-color-placeholder);
  font-size: 13px;
}

.field-hint {
  margin-left: 10px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.original-text-actions {
  margin-top: 10px;
}

.typing-text-toolbar {
  margin-bottom: 10px;
}

.sample-title-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sample-title-item {
  padding: 10px 12px;
  margin: 0;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.sample-title-item:hover {
  background: var(--el-fill-color-light);
}

.sample-title-main {
  min-width: 0;
  flex: 1;
  cursor: pointer;
}

.sample-title-text {
  color: var(--el-text-color-primary);
  font-weight: 500;
}

.sample-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.sample-subtitle-gap {
  margin: 0 6px;
}

.sample-actions {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.form-item-tip {
  margin: 0 0 10px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.55;
}
</style>
